import { NavLink, useNavigate } from 'react-router-dom'
import { ChevronDown, User } from 'lucide-react'
import { SearchInput } from '@datahub/design-system'
import { useState, type ReactNode } from 'react'
import { post } from '../lib/api'
import type { AdminUser } from '../lib/types'

const BORDER = '0.5px solid #4B5563'

const NAV = [
  {
    title: 'Datahub',
    items: [
      { label: 'Dashboard', to: '/admin' },
      { label: 'Companies', to: '/admin/companies' },
      { label: 'Funds', to: '/admin/funds' },
      { label: 'Cycles', to: '/admin/cycles' },
      { label: 'Submissions', to: '/admin/submissions' },
      { label: 'Reports', to: '/admin/reports' },
      { label: 'Settings', to: '/admin/settings' },
    ],
  },
]

/** Global Shell: 80px TopBar, 256px Sidebar, 20px canvas (plan Section 12.4). */
export function AdminShell({ admin, children }: { admin: AdminUser; children: ReactNode }) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [query, setQuery] = useState('')

  const search = (value: string) => {
    setQuery(value)
    if (value.trim().length < 2) return
    // Search from wherever the admin is: the Funds pages search funds, everything else
    // searches companies.
    const target = window.location.pathname.startsWith('/admin/funds') ? '/admin/funds' : '/admin/companies'
    navigate(`${target}?q=${encodeURIComponent(value.trim())}`)
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 flex items-stretch bg-background-main" style={{ height: 80 }}>
        <div
          className="flex items-center flex-shrink-0 bg-background-main"
          style={{ width: 256, paddingLeft: 20, paddingRight: 32, borderRight: BORDER, borderBottom: BORDER }}
        >
          <img src="/images/stears-logo-white.webp" alt="Stears" style={{ height: 24, width: 'auto' }} />
        </div>
        <div
          className="flex flex-1 items-center justify-between bg-background-main"
          style={{ paddingLeft: 20, paddingRight: 20, borderBottom: BORDER }}
        >
          <SearchInput size="large" placeholder="Search companies, funds and submissions" className="w-[600px] max-w-[50vw]" value={query} onChange={search} />
          <div className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center bg-button-primary text-white font-jakarta gap-2 hover:opacity-90 flex-shrink-0"
              style={{ height: 40, padding: '8px 20px', fontSize: 12, border: 'none', cursor: 'pointer' }}
            >
              <User size={16} />
              <span>{admin.displayName ?? admin.email}</span>
              <ChevronDown size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 bg-background-body" style={{ top: 44, border: BORDER, minWidth: 220, zIndex: 60 }}>
                <div className="font-mulish text-text-support" style={{ padding: '8px 12px', fontSize: 10, borderBottom: BORDER }}>
                  {admin.email}
                  {admin.isBootstrap && ' · default admin'}
                </div>
                <button
                  onClick={async () => { await post('/auth/logout'); window.location.href = '/admin' }}
                  className="w-full text-left font-mulish text-text-support hover:bg-background-skeleton"
                  style={{ padding: '8px 12px', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <aside
        className="fixed left-0 bottom-0 bg-background-main overflow-y-auto flex flex-col"
        style={{ width: 256, top: 80, borderRight: BORDER }}
      >
        {NAV.map((section, i) => (
          <div key={section.title} className="bg-background-main w-full flex-shrink-0" style={{ borderBottom: i === NAV.length - 1 ? undefined : BORDER, paddingBottom: 20 }}>
            <div className="flex items-end overflow-hidden" style={{ height: 24, padding: '4px 20px 0' }}>
              <span className="font-jakarta text-text-support whitespace-nowrap" style={{ fontSize: 10, lineHeight: '12px', letterSpacing: '0.2px' }}>
                {section.title}
              </span>
            </div>
            <ul style={{ paddingTop: 4, listStyle: 'none', margin: 0 }}>
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/admin'}
                    className="flex items-center justify-between w-full font-jakarta font-medium hover:bg-background-body transition-colors"
                    style={({ isActive }) => ({
                      fontSize: 14,
                      lineHeight: '22px',
                      padding: '5px 20px',
                      color: isActive ? '#FFFFFF' : '#9CA3AF',
                      backgroundColor: isActive ? '#1A1D21' : undefined,
                      textDecoration: 'none',
                    })}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </aside>

      <main
        className="bg-background-body"
        style={{ marginTop: 80, marginLeft: 256, height: 'calc(100vh - 80px)', overflow: 'hidden' }}
      >
        {children}
      </main>
    </>
  )
}

/** Entity List page frame: 58px heading row, then the page's own content. */
export function ListPage({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col bg-background-body" style={{ padding: 20, height: '100%', overflow: 'hidden' }}>
      <div className="flex items-center justify-between flex-shrink-0" style={{ height: 58 }}>
        <h1 className="font-jakarta font-bold text-white" style={{ fontSize: 20, lineHeight: '24px' }}>{title}</h1>
        <div className="flex items-center" style={{ gap: 8 }}>{actions}</div>
      </div>
      <div className="flex-1 flex flex-col" style={{ overflow: 'hidden', border: BORDER }}>{children}</div>
    </div>
  )
}
